import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';

interface Project { id: string; code: string; name: string; status: string; }

interface Props {
  onClose: () => void;
  dnaId?: string;
  dnaCode?: string | null;
  workerName: string;
  workerId: string;
  initial?: {
    projectId?: string;
    scheduledDate?: string;
    scheduledTime?: string | null;
    recordedBy?: string;
    notes?: string | null;
  };
}

export function DnaEditModal({ onClose, dnaId, dnaCode, workerName, workerId, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!dnaId;
  const [projectId, setProjectId] = useState(initial?.projectId ?? '');
  const [date, setDate] = useState(initial?.scheduledDate?.slice(0, 10) ?? '');
  const [time, setTime] = useState(initial?.scheduledTime ?? '');
  const [recordedBy, setRecordedBy] = useState(initial?.recordedBy ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const selectedProject = projects.find((p) => p.id === projectId);
  const projectLabel = selectedProject?.code ?? projectId;

  const save = useMutation({
    mutationFn: async () => {
      const body = { workerId, projectId, scheduledDate: date, scheduledTime: time || null, recordedBy, notes: notes.trim() || null };
      if (editing) return api.patch(`/dna-records/${dnaId}`, body);
      return api.post('/dna-records', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments', 'dna'] });
      qc.invalidateQueries({ queryKey: ['dna-records'] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      toast.success(editing ? 'DNA updated' : 'DNA recorded');
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="modal-title" id="dna-edit-title">{editing ? `Edit DNA Record${dnaCode ? ` — ${dnaCode}` : ''}` : 'Add DNA Record'}</div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }} id="dna-edit-worker-label">
          <strong>{workerName}</strong>{editing && projectLabel ? ` · ${projectLabel}` : ''}{editing && date ? ` · ${date}` : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label className="form-label">Project <span style={{ color: '#ef4444' }}>*</span></label>
            <SearchableSelect
              id="dna-edit-project"
              value={projectId}
              onChange={setProjectId}
              placeholder="— Select project —"
              options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
            />
          </div>
          <div>
            <label className="form-label">Scheduled Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="date" className="form-input" id="dna-edit-date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: '100%' }} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label className="form-label">Scheduled Time</label>
          <input type="time" className="form-input" id="dna-edit-time" value={time ?? ''} onChange={(e) => setTime(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label className="form-label">Recorded By <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="form-input" id="dna-edit-recorded-by" placeholder="Manager recording this DNA" value={recordedBy} onChange={(e) => setRecordedBy(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="form-label">Notes</label>
          <textarea className="form-textarea" id="dna-edit-notes" placeholder="Reason, contact attempts, outcome…" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ minHeight: 70, width: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: '#ef4444', borderColor: '#ef4444' }} onClick={() => save.mutate()} disabled={save.isPending || !projectId || !date || !recordedBy}>
            {save.isPending ? 'Saving…' : '⚠ Save DNA Record'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
